import React, { useEffect, useState, useContext, useMemo } from "react";
import Navbar from "../../layout/Navbar/Navbar";
import Sidebar from "../../layout/Sidebar/SideBar.jsx";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import SearchIcon from "@mui/icons-material/Search";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import AddModeratorIcon from "@mui/icons-material/AddModerator";
import blogFetch from "../../axios/config";
import {
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import UserGroupsModal from "./GroupPermissionModal";
import { IGroup } from "./GroupPermissionInterfaces";
import ModalAddGroup from "./RegisterGroup";
import ModalAddPermission from "./RegisterPermission";
import Context from "../../context/Context";
import language from "../../nls/language.json";

export default function Group() {
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showModalAddGroup, setShowModalAddGroup] = useState(false);
  const [showModalAddPermission, setShowModalAddPermission] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<IGroup | undefined>(
    undefined
  );
  const [search, setSearch] = useState("");

  const {
    getStatusSideBar,
    selectedCountry
  } = useContext(Context);

  const lang = (language as Record<string, any>)[selectedCountry.name].Permissoes;

  useEffect(() => {
    getGroups();
  }, []);

  const getGroups = async () => {
    try {
      let response = await blogFetch.get("/group");
      let data: IGroup[] = response.data;
      setGroups(data);
    } catch (error) {
      console.error(error);
    }
  };

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const term = search.toLowerCase().trim();
    return groups.filter((g) =>
      g.group_description?.toLowerCase().includes(term)
    );
  }, [groups, search]);

  const handleRowClick = (group: IGroup) => {
    setSelectedGroup(group);
    setShowModal(true);
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <div className={`app_body ${getStatusSideBar()}`}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <TextField
            size="small"
            placeholder="Pesquisar grupo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#f8f9fa",
                fontSize: 13,
                "&:hover fieldset": { borderColor: "#9ed6de" },
                "&.Mui-focused fieldset": { borderColor: "#9ed6de" },
              },
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Typography
              variant="body2"
              sx={{ color: "#888", fontSize: 12, whiteSpace: "nowrap" }}
            >
              {filteredGroups.length} de {groups.length} grupos
            </Typography>
            <Button
              variant="contained"
              startIcon={<GroupAddIcon />}
              onClick={() => setShowModalAddGroup(true)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontSize: 13,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {lang.grupos.btnAdicionarGrupo}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddModeratorIcon />}
              onClick={() => setShowModalAddPermission(true)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontSize: 13,
                boxShadow: "none",
              }}
            >
              {lang.grupos.btnAdicionarPermissao}
            </Button>
          </div>
        </div>

        <TableContainer
          sx={{
            borderRadius: "8px",
            border: "1px solid #e8e8e8",
            maxHeight: "calc(100vh - 210px)",
            overflow: "auto",
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "#3d3d3d",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 12,
                    padding: "10px 14px",
                    borderBottom: "none",
                  }}
                >
                  {lang.grupos.tableHeader.nome}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#3d3d3d",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 12,
                    padding: "10px 14px",
                    borderBottom: "none",
                    width: 80,
                  }}
                >
                  {lang.grupos.tableHeader.editar}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGroups.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      textAlign: "center",
                      padding: "32px 14px",
                      color: "#999",
                      fontSize: 13,
                    }}
                  >
                    {search.trim()
                      ? `Nenhum grupo encontrado para "${search}"`
                      : "Nenhum grupo cadastrado"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredGroups.map((group, index) => (
                  <TableRow
                    key={group.group_Id}
                    onClick={() => handleRowClick(group)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                      transition: "background-color 0.15s",
                      "&:hover": { backgroundColor: "#e8f6f8" },
                      "& td": {
                        fontSize: 13,
                        padding: "9px 14px",
                        borderBottom: "1px solid #eee",
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {group.group_description}
                    </TableCell>
                    <TableCell align="center">
                      <ModeEditIcon sx={{ fontSize: 18, color: "#757575" }} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <UserGroupsModal
        open={showModal}
        onClose={() => setShowModal(false)}
        group={selectedGroup}
      />
      <ModalAddGroup
        open={showModalAddGroup}
        onClose={() => setShowModalAddGroup(false)}
        getGroup={getGroups}
      />
      <ModalAddPermission
        open={showModalAddPermission}
        onClose={() => setShowModalAddPermission(false)}
        getGroup={getGroups}
      />
    </>
  );
}
